import React, { useState, useRef, useEffect } from 'react';
import { UserCheck } from 'lucide-react';
import { Drawer } from '../common/Drawer';
import { Button } from '../common/Button';
import { CustomSelect } from '../common/CustomSelect';
import { useDatabase } from '../../db/DatabaseProvider';
import { useAuthStore } from '../../stores';
import { useBranches, useLookupMutations, useStaffRoles } from '../../db/hooks/useLookupData';
import { useToast } from '../common/ToastProvider';
import { now } from '../../lib/uuid';
import { getValidBranchId } from '../../lib/utils';
import { WEEKDAYS } from './constants';

export interface StaffFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (createdStaff: StaffDocType) => void;
  staffToEdit?: StaffDocType | null;
  defaultBranchIdOverride?: string;
}

export function StaffFormDrawer({
  isOpen,
  onClose,
  onSuccess,
  staffToEdit,
  defaultBranchIdOverride,
}: StaffFormDrawerProps): React.ReactNode {
  const db = useDatabase();
  const user = useAuthStore((s) => s.user);
  const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
  const isOwner = useAuthStore((s) => s.isOwner);
  const toast = useToast();

  const { branches } = useBranches();
  const { addStaff } = useLookupMutations();
  const { roles, addRole } = useStaffRoles();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [customRoleInput, setCustomRoleInput] = useState('');
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [defaultBranchId, setDefaultBranchId] = useState('');
  const [sueldoMensual, setSueldoMensual] = useState<number | ''>('');
  const [payFrequency, setPayFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('biweekly');
  const [isActive, setIsActive] = useState(true);

  // 7-day schedule state ('is_active' | 'libre' | 'no_asiste')
  const [diasTrabajo, setDiasTrabajo] = useState<Record<string, 'is_active' | 'libre' | 'no_asiste'>>({
    lunes: 'is_active',
    martes: 'is_active',
    miercoles: 'is_active',
    jueves: 'is_active',
    viernes: 'is_active',
    sabado: 'is_active',
    domingo: 'no_asiste',
  });

  useEffect(() => {
    if (isOpen) {
      if (staffToEdit) {
        setName(staffToEdit.name || '');
        setRole(staffToEdit.role || '');
        setCustomRoleInput('');
        setIsCreatingRole(false);
        setPhone(staffToEdit.phone || '');
        setEmail(staffToEdit.email || '');
        setDefaultBranchId(
          staffToEdit.default_branch_id ||
          defaultBranchIdOverride ||
          selectedBranchId ||
          (branches.length > 0 ? branches[0].id : '')
        );
        setSueldoMensual(staffToEdit.monthly_salary ?? '');
        setPayFrequency((staffToEdit.pay_frequency as any) || 'biweekly');
        setIsActive(staffToEdit.is_active !== false);
        if (staffToEdit.work_days) {
          setDiasTrabajo({ ...staffToEdit.work_days } as any);
        } else {
          setDiasTrabajo({
            lunes: 'is_active',
            martes: 'is_active',
            miercoles: 'is_active',
            jueves: 'is_active',
            viernes: 'is_active',
            sabado: 'is_active',
            domingo: 'no_asiste',
          });
        }
      } else {
        setName('');
        setRole('');
        setCustomRoleInput('');
        setIsCreatingRole(false);
        setPhone('');
        setEmail('');
        setDefaultBranchId(
          defaultBranchIdOverride ||
          selectedBranchId ||
          (branches.length > 0 ? branches[0].id : '')
        );
        setSueldoMensual('');
        setPayFrequency('biweekly');
        setIsActive(true);
        setDiasTrabajo({
          lunes: 'is_active',
          martes: 'is_active',
          miercoles: 'is_active',
          jueves: 'is_active',
          viernes: 'is_active',
          sabado: 'is_active',
          domingo: 'no_asiste',
        });
      }

      // Auto-focus name input
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, staffToEdit, defaultBranchIdOverride, selectedBranchId, branches]);

  const cycleDayStatus = (current: 'is_active' | 'libre' | 'no_asiste'): 'is_active' | 'libre' | 'no_asiste' => {
    if (current === 'is_active') return 'libre';
    if (current === 'libre') return 'no_asiste';
    return 'is_active';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      let finalRole = role;
      if (isCreatingRole && customRoleInput.trim()) {
        finalRole = customRoleInput.trim();
        await addRole(finalRole);
      }

      const validBranchId = getValidBranchId(
        defaultBranchId,
        defaultBranchIdOverride || selectedBranchId,
        branches
      );

      if (!validBranchId) {
        toast.error('Debe existir al menos una sucursal registrada para asignar al staff.', 'Sucursal Requerida');
        return;
      }

      if (staffToEdit) {
        const doc = await db.staff.findOne(staffToEdit.id).exec();
        if (doc) {
          await doc.patch({
            name: name.trim(),
            role: finalRole ? finalRole.trim() : null,
            phone: phone ? phone.trim() : null,
            email: email ? email.trim() : null,
            default_branch_id: validBranchId,
            monthly_salary: sueldoMensual === '' ? null : Number(sueldoMensual),
            pay_frequency: payFrequency,
            work_days: diasTrabajo,
            is_active: isActive,
            updated_at: now(),
            last_modified_by: user?.email || 'system',
          });
          const updatedStaff = (typeof (doc as any).toJSON === 'function' ? (doc as any).toJSON() : doc) as StaffDocType;
          toast.success('Datos del staff actualizados correctamente');
          onSuccess?.(updatedStaff);
          onClose();
        }
      } else {
        const createdStaff = await addStaff({
          name: name.trim(),
          default_branch_id: validBranchId,
          role: finalRole ? finalRole.trim() : null,
          phone: phone ? phone.trim() : null,
          email: email ? email.trim() : null,
          monthly_salary: sueldoMensual === '' ? null : Number(sueldoMensual),
          pay_frequency: payFrequency,
          work_days: diasTrabajo,
        });
        toast.success('Miembro del staff registrado exitosamente');
        const staffResult = (typeof (createdStaff as any)?.toJSON === 'function'
          ? (createdStaff as any).toJSON()
          : createdStaff) as StaffDocType;
        onSuccess?.(staffResult);
        onClose();
      }
    } catch (err: any) {
      toast.error('Error al guardar staff: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={staffToEdit ? 'Editar Miembro del Personal' : 'Registrar Nuevo Miembro'}
      subtitle="Asigna rol, sueldo, sucursal principal y horario laboral"
      icon={UserCheck}
      maxWidth="lg"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            form="staff-form"
            isLoading={isSubmitting}
          >
            {staffToEdit ? 'Actualizar Miembro' : 'Guardar Miembro'}
          </Button>
        </>
      }
    >
      <form id="staff-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-1">Nombre Completo *</label>
          <input
            ref={nameInputRef}
            required
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
            placeholder="Ej. Ana Rodríguez"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Rol / Cargo *</label>
            {isCreatingRole ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={customRoleInput}
                  onChange={(e) => setCustomRoleInput(e.target.value)}
                  placeholder="Nuevo rol..."
                  className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
                />
                <button
                  type="button"
                  onClick={() => setIsCreatingRole(false)}
                  className="px-3 py-2.5 text-xs font-bold text-text-secondary hover:bg-surface-hover rounded-xl border border-border cursor-pointer"
                >
                  Volver
                </button>
              </div>
            ) : (
              <CustomSelect
                value={role}
                onChange={(val) => {
                  if (val === '__new__') {
                    setIsCreatingRole(true);
                    setCustomRoleInput('');
                  } else {
                    setRole(val);
                  }
                }}
                options={[
                  ...roles.map((r) => ({ value: r, label: r })),
                  { value: '__new__', label: '+ Crear Nuevo Rol' },
                ]}
              />
            )}
          </div>

          {isOwner && (
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Sucursal Principal *</label>
              <CustomSelect
                value={defaultBranchId}
                onChange={setDefaultBranchId}
                options={branches.map((s) => ({ value: s.id, label: s.name }))}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Teléfono</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              placeholder="Ej. +58 424-9876543"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Correo Electrónico</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              placeholder="Ej. ana@depilaway.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Sueldo Asignado</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={sueldoMensual}
              onChange={(e) => setSueldoMensual(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Frecuencia de Pago</label>
            <CustomSelect
              value={payFrequency}
              onChange={(val) => setPayFrequency(val as any)}
              options={[
                { value: 'weekly', label: 'Semanal' },
                { value: 'biweekly', label: 'Quincenal' },
                { value: 'monthly', label: 'Mensual' },
              ]}
            />
          </div>
        </div>

        {/* Schedule Section */}
        <div className="p-3.5 rounded-xl bg-surface-tertiary/40 border border-border/60 space-y-2">
          <label className="block text-xs font-bold text-text-primary">Horario Semanal de Trabajo</label>
          <p className="text-[11px] text-text-tertiary">Haz clic en cada día para alternar su status laboral:</p>
          <div className="grid grid-cols-7 gap-1.5 pt-1">
            {WEEKDAYS.map((day) => {
              const status = diasTrabajo[day.key] || 'is_active';
              const statusLabel =
                status === 'is_active'
                  ? 'Laboral'
                  : status === 'libre'
                  ? 'Día Libre (Remunerado)'
                  : 'No Laboral';

              return (
                <button
                  key={day.key}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    const next = cycleDayStatus(status);
                    setDiasTrabajo({ ...diasTrabajo, [day.key]: next });
                  }}
                  title={`${day.fullLabel}: ${statusLabel}`}
                  className={`py-2 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                    status === 'is_active'
                      ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                      : status === 'libre'
                      ? 'bg-brand-orange/10 text-brand-orange border-brand-orange/20'
                      : 'bg-surface-secondary text-text-tertiary border-border'
                  }`}
                >
                  <div>{day.label}</div>
                  <div className="text-[9px] mt-0.5 font-semibold">
                    {status === 'is_active' ? 'Labora' : status === 'libre' ? 'Libre' : 'No asiste'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </form>
    </Drawer>
  );
}
